/**
 * Traveller Data Transformers
 *
 * Pure functions for transforming traveller data between different formats:
 * - API response → Redux state
 * - Redux state → Form values
 * - Form values → API request
 */
import type {
  PaxType,
  TravellerItem,
  TravellerData,
  FormValues,
  SavedTravellersState,
  ContactDetailsFormValues,
  GstDetailsFormValues,
} from '@contracts';

// ============================================
// Types
// ============================================

/** Associated traveller format for API requests */
export interface AssociatedTraveller {
  travellerId: string;
  infoDetail: {
    rowFields: Record<string, string>;
  };
  paxType: PaxType;
  userPrimaryTravellerId: number;
  isSelected: boolean;
}

/** Prebook payload structure */
export interface PrebookPayload {
  associatedTravellers: AssociatedTraveller[];
  contactDetails: {
    rowFields: {
      COUNTRY_CODE: string;
      COUNTRY_NAME: string;
      EMAIL: string;
      MOBILE_NUMBER: string;
    };
  };
  gstDetails: {
    rowFields: {
      REGISTERED_COMPANY_NAME: string;
      REGISTRATION_NO: string;
    };
    optIn: boolean;
  };
  travellerBillingAddressDetails: {
    state: string;
    stateId: string;
  };
  itineraryId: string;
}

// ============================================
// Constants
// ============================================

/** Ordered list of pax types for consistent iteration */
export const PAX_TYPES: readonly PaxType[] = ['ADULT', 'CHILD', 'INFANT'] as const;

// ============================================
// Transformers: Redux → Form
// ============================================

/**
 * Convert a single TravellerItem to TravellerData (form format)
 */
export function travellerItemToFormData(traveller: TravellerItem): TravellerData {
  return {
    ...traveller.rowFields,
    _savedId: traveller.id,
  };
}

/**
 * Convert SavedTravellersState to FormValues
 * Only includes pre-selected travellers
 */
export function savedTravellersToFormValues(savedTravellers: SavedTravellersState): FormValues {
  const formValues: FormValues = {};

  for (const paxType of PAX_TYPES) {
    const travellers = savedTravellers[paxType] ?? [];
    const preSelected = travellers.filter((t) => t.isPreSelected);
    formValues[paxType] = preSelected.map(travellerItemToFormData);
  }

  return formValues;
}

/**
 * Get pre-selected travellers for a specific pax type
 */
export function getPreSelectedTravellers(
  savedTravellers: SavedTravellersState,
  paxType: PaxType,
): TravellerData[] {
  const travellers = savedTravellers[paxType] ?? [];
  return travellers.filter((t) => t.isPreSelected).map(travellerItemToFormData);
}

// ============================================
// Transformers: Form/Redux → API Request
// ============================================

/**
 * Convert a TravellerItem to AssociatedTraveller format for API
 */
export function travellerItemToAssociatedTraveller(
  traveller: TravellerItem,
  paxType: PaxType,
  index: number,
): AssociatedTraveller {
  return {
    travellerId: traveller.id,
    infoDetail: {
      rowFields: { ...traveller.rowFields },
    },
    paxType,
    userPrimaryTravellerId: index + 1,
    isSelected: true,
  };
}

/**
 * Convert SavedTravellersState to AssociatedTravellers array
 * Only includes pre-selected travellers
 */
export function savedTravellersToAssociatedTravellers(
  savedTravellers: SavedTravellersState,
): AssociatedTraveller[] {
  const associatedTravellers: AssociatedTraveller[] = [];

  for (const paxType of PAX_TYPES) {
    const travellers = savedTravellers[paxType] ?? [];

    for (const traveller of travellers) {
      if (traveller.isPreSelected) {
        associatedTravellers.push(
          travellerItemToAssociatedTraveller(traveller, paxType, associatedTravellers.length),
        );
      }
    }
  }

  return associatedTravellers;
}

/**
 * Convert FormValues to AssociatedTravellers array
 */
export function formValuesToAssociatedTravellers(formData: FormValues): AssociatedTraveller[] {
  const associatedTravellers: AssociatedTraveller[] = [];

  for (const paxType of Object.keys(formData) as PaxType[]) {
    const travellers = formData[paxType] ?? [];

    for (const traveller of travellers) {
      const { _savedId, ...rowFields } = traveller;

      associatedTravellers.push({
        travellerId: _savedId,
        infoDetail: { rowFields },
        paxType,
        userPrimaryTravellerId: associatedTravellers.length + 1,
        isSelected: true,
      });
    }
  }

  return associatedTravellers;
}

// ============================================
// Prebook Payload Builder
// ============================================

/**
 * Build complete prebook payload from saved state
 * Returns null if no travellers are selected
 */
export function buildPrebookPayload(
  savedTravellers: SavedTravellersState,
  contactDetails: ContactDetailsFormValues,
  gstDetails: GstDetailsFormValues,
  itineraryId: string | null,
): PrebookPayload | null {
  const associatedTravellers = savedTravellersToAssociatedTravellers(savedTravellers);

  // Return null if no travellers are selected
  if (associatedTravellers.length === 0) {
    return null;
  }

  return {
    associatedTravellers,
    contactDetails: {
      rowFields: {
        COUNTRY_CODE: contactDetails.COUNTRY_CODE,
        COUNTRY_NAME: contactDetails.COUNTRY_NAME,
        EMAIL: contactDetails.EMAIL,
        MOBILE_NUMBER: contactDetails.MOBILE_NUMBER,
      },
    },
    gstDetails: {
      rowFields: {
        REGISTERED_COMPANY_NAME: gstDetails.REGISTERED_COMPANY_NAME,
        REGISTRATION_NO: gstDetails.REGISTRATION_NO,
      },
      optIn: gstDetails.optIn,
    },
    travellerBillingAddressDetails: {
      state: 'Other/Outside India',
      stateId: 'OTH',
    },
    itineraryId: itineraryId ?? '',
  };
}

// ============================================
// Utility Functions
// ============================================

/**
 * Check if SavedTravellersState has any travellers
 */
export function hasSavedTravellers(savedTravellers: SavedTravellersState): boolean {
  return Object.keys(savedTravellers).length > 0;
}

/**
 * Check if SavedTravellersState has any pre-selected travellers
 */
export function hasPreSelectedTravellers(savedTravellers: SavedTravellersState): boolean {
  for (const paxType of PAX_TYPES) {
    const travellers = savedTravellers[paxType] ?? [];
    if (travellers.some((t) => t.isPreSelected)) {
      return true;
    }
  }
  return false;
}

/**
 * Count total pre-selected travellers across all pax types
 */
export function countPreSelectedTravellers(savedTravellers: SavedTravellersState): number {
  let count = 0;
  for (const paxType of PAX_TYPES) {
    const travellers = savedTravellers[paxType] ?? [];
    count += travellers.filter((t) => t.isPreSelected).length;
  }
  return count;
}
